import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { computeDelta, type DeltaResult } from "@/lib/recovery/trend";

/**
 * Reads 28 days of vitals + nutrition rolls and returns 7-day deltas for
 * every metric a card on /today might display. Single query, single hook,
 * shared cache (BW18 PR-F).
 *
 * Card primitives consume `useTrendDeltas().data?.<metric>` and pass the
 * result to the optional `delta` prop on the card.
 */
export type DeltasShape = {
  hrv_ms: DeltaResult;
  rhr_bpm: DeltaResult;
  sleep_h: DeltaResult;
  steps: DeltaResult;
  active_kcal: DeltaResult;
  weight_kg: DeltaResult;
  protein_g: DeltaResult;
  fiber_g: DeltaResult;
  kcal_total: DeltaResult;
};

export function useTrendDeltas() {
  const { session } = useSession();
  const userId = session?.user?.id;

  return useQuery<DeltasShape>({
    queryKey: ["trend_deltas", userId],
    enabled: Boolean(userId),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      if (!userId) {
        return emptyDeltas();
      }
      const since = new Date();
      since.setDate(since.getDate() - 28);
      const sinceIso = since.toISOString().slice(0, 10);

      const [vitalsRes, sleepRes, bodyRes, nutritionRes] = await Promise.all([
        supabase
          .from("daily_vitals")
          .select("date, hrv_status_ms, rhr_bpm, steps, calories_active")
          .eq("user_id", userId)
          .gte("date", sinceIso)
          .order("date", { ascending: true }),
        supabase
          .from("sleep_record")
          .select("date, duration_min")
          .eq("user_id", userId)
          .gte("date", sinceIso)
          .order("date", { ascending: true }),
        supabase
          .from("body_measurement")
          .select("ts, weight_kg")
          .eq("user_id", userId)
          .gte("ts", `${sinceIso}T00:00:00Z`)
          .order("ts", { ascending: true }),
        supabase
          .from("nutrition_roll")
          .select("date, kcal_total, protein_g, fiber_g")
          .eq("user_id", userId)
          .gte("date", sinceIso)
          .order("date", { ascending: true }),
      ]);

      const vitals = vitalsRes.data ?? [];
      const sleep = sleepRes.data ?? [];
      const body = bodyRes.data ?? [];
      const nutrition = nutritionRes.data ?? [];

      const numbersOf = <T,>(rows: T[], pick: (r: T) => number | null | undefined) =>
        rows
          .map(pick)
          .filter((n): n is number => typeof n === "number" && !Number.isNaN(n));

      return {
        hrv_ms: computeDelta(numbersOf(vitals, (v) => v.hrv_status_ms)),
        rhr_bpm: computeDelta(numbersOf(vitals, (v) => v.rhr_bpm)),
        sleep_h: computeDelta(
          numbersOf(sleep, (s) => (s.duration_min != null ? s.duration_min / 60 : null)),
        ),
        steps: computeDelta(numbersOf(vitals, (v) => v.steps)),
        active_kcal: computeDelta(numbersOf(vitals, (v) => v.calories_active)),
        weight_kg: computeDelta(numbersOf(body, (b) => Number(b.weight_kg))),
        protein_g: computeDelta(numbersOf(nutrition, (n) => n.protein_g)),
        fiber_g: computeDelta(numbersOf(nutrition, (n) => n.fiber_g)),
        kcal_total: computeDelta(numbersOf(nutrition, (n) => n.kcal_total)),
      };
    },
  });
}

function emptyDeltas(): DeltasShape {
  const e: DeltaResult = {
    current: null,
    avg_7d: null,
    avg_28d: null,
    delta_7d: null,
    delta_28d: null,
  };
  return {
    hrv_ms: e,
    rhr_bpm: e,
    sleep_h: e,
    steps: e,
    active_kcal: e,
    weight_kg: e,
    protein_g: e,
    fiber_g: e,
    kcal_total: e,
  };
}
